import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Wallet, ArrowDownToLine, Clock, CheckCircle2, XCircle,
  CreditCard, Building2, DollarSign, Download, TrendingUp, Calendar,
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { walletApi } from '../../api/endpoints/wallet';
import { StatCard } from '../../components/ui/StatCard';
import { Tabs } from '../../components/ui/Tabs';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { useToast } from '../../components/ui/Alert';
import { PermissionGate } from '../../components/ui/PermissionGate';
import { PERMISSIONS } from '../../lib/permissions';
import { formatCurrency, formatDateTime, formatDate } from '../../lib/formatters';
import { CurrencyDisplay } from '../../components/ui/CurrencyDisplay';
import type { WithdrawInput, WithdrawalAccount, WalletTransaction } from '../../types/wallet';

const ACCOUNT_CATEGORIES = ['paypal', 'alrajhi', 'otherbank'] as const;

const withdrawSchema = z.object({
  // 'alinma' is kept here only so already-saved legacy accounts can still be
  // reused/submitted; it's no longer offered as a new option.
  accountCategory: z.enum(['paypal', 'alrajhi', 'otherbank', 'alinma']),
  accountIdentifier: z.string().min(1),
  legalName: z.string().min(1),
  bankName: z.string().optional(),
});

type WithdrawFormValues = z.infer<typeof withdrawSchema>;

function buildCsvContent(transactions: WalletTransaction[], lang: string): string {
  const header = 'Date,Gross,Fees,Net,Currency';
  const rows = transactions.map((tx) => {
    const date = tx.createdAt ? formatDate(tx.createdAt, 'yyyy-MM-dd', lang) : '';
    const gross = tx.originalAmount.toFixed(2);
    const fee = (tx.originalAmount - tx.amount).toFixed(2);
    const net = tx.amount.toFixed(2);
    return `${date},${gross},${fee},${net},${tx.currency}`;
  });
  return [header, ...rows].join('\n');
}

function downloadCsv(content: string, filename: string) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function WalletPage() {
  const { storeId } = useParams<{ storeId: string }>();
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const lang = i18n.language;

  const [tab, setTab] = useState('transactions');
  const [chartPeriod, setChartPeriod] = useState<'months' | 'years'>('months');
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<string | null>(null);

  const { data: overview, isLoading: overviewLoading } = useQuery({
    queryKey: ['wallet', storeId],
    queryFn: () => walletApi.getOverview(storeId!),
    enabled: !!storeId,
  });

  const { data: transactions, isLoading: txLoading } = useQuery({
    queryKey: ['wallet-transactions', storeId],
    queryFn: () => walletApi.getTransactions(storeId!),
    enabled: !!storeId && tab === 'transactions',
  });

  const { data: earningsData } = useQuery({
    queryKey: ['wallet-earnings', storeId, chartPeriod],
    queryFn: () => walletApi.getEarnings(storeId!, chartPeriod),
    enabled: !!storeId,
    staleTime: 60000,
  });

  const withdrawMutation = useMutation({
    mutationFn: (data: WithdrawInput) => walletApi.withdraw(storeId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wallet', storeId] });
      queryClient.invalidateQueries({ queryKey: ['wallet-transactions', storeId] });
      toast('success', t('wallet.withdrawSuccess'));
      setShowWithdrawModal(false);
      form.reset();
      setSelectedAccount(null);
    },
    onError: () => toast('error', t('common.error')),
  });

  const form = useForm<WithdrawFormValues>({
    resolver: zodResolver(withdrawSchema),
    defaultValues: { accountCategory: 'alrajhi', accountIdentifier: '', legalName: '' },
  });

  const watchCategory = form.watch('accountCategory');

  /* ---------- Export handler ---------- */
  const handleExport = useCallback(() => {
    if (!transactions?.length) return;
    const csv = buildCsvContent(transactions, lang);
    const dateStr = formatDate(new Date(), 'yyyy-MM-dd', lang);
    downloadCsv(csv, `wallet-transactions-${dateStr}.csv`);
  }, [transactions, lang]);

  const onSubmitWithdraw = (values: WithdrawFormValues) => {
    const input: WithdrawInput = {
      accountCategory: values.accountCategory,
      accountIdentifier: values.accountIdentifier,
      legalName: values.legalName,
      bankName: values.bankName,
    };
    if (selectedAccount) input.accountId = selectedAccount;
    withdrawMutation.mutate(input);
  };

  const selectExistingAccount = (acc: WithdrawalAccount) => {
    setSelectedAccount(acc._id);
    form.setValue('accountCategory', acc.category);
    form.setValue('accountIdentifier', acc.identifier);
    form.setValue('legalName', acc.legalName);
    if (acc.bankName) form.setValue('bankName', acc.bankName);
  };

  if (overviewLoading) return <LoadingSpinner fullPage />;

  const balance = overview?.balance ?? 0;
  const currency = overview?.currency ?? 'SAR';
  const withdrawalHistory = overview?.withdrawalHistory ?? [];
  const withdrawalAccounts = overview?.withdrawalAccounts ?? [];
  const hasPending = withdrawalHistory.some((w) => w.state === 'pending');

  const stateIcon = (state: string) => {
    switch (state) {
      case 'confirmed':
      case 'completed': return <CheckCircle2 className="h-4 w-4 text-success" />;
      case 'failed': return <XCircle className="h-4 w-4 text-danger" />;
      default: return <Clock className="h-4 w-4 text-warning" />;
    }
  };

  const stateVariant = (state: string) => {
    switch (state) {
      case 'confirmed':
      case 'completed': return 'success' as const;
      case 'failed': return 'danger' as const;
      default: return 'warning' as const;
    }
  };

  const PAYMENT_LABELS: Record<string, string> = {
    moyasar: t('wallet.cardPayment', 'Card Payment'),
    clickpay: t('wallet.cardPayment', 'Card Payment'),
    paypal: 'PayPal',
    stcpay: 'STC Pay',
    bank_transfer: t('wallet.bankTransfer', 'Bank Transfer'),
    cod: t('wallet.cod', 'Cash on Delivery'),
  };

  const paymentLabel = (method: string) => {
    if (PAYMENT_LABELS[method]) return PAYMENT_LABELS[method];
    const key = `wallet.${method}` as const;
    const translated = t(key);
    return translated !== key ? translated : method;
  };

  const tabs = [
    { key: 'transactions', label: t('wallet.transactions'), icon: <CreditCard className="h-4 w-4" /> },
    { key: 'withdrawals', label: t('wallet.withdrawalHistory'), icon: <ArrowDownToLine className="h-4 w-4" /> },
  ];

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl font-bold">{t('wallet.title')}</h1>
        <PermissionGate requires={PERMISSIONS.WALLET_ANY}>
          <Button
            onClick={() => setShowWithdrawModal(true)}
            disabled={balance < 50 || hasPending}
          >
            <ArrowDownToLine className="h-4 w-4" />
            {t('wallet.requestWithdrawal')}
          </Button>
        </PermissionGate>
      </div>

      {/* Balance cards */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard
          title={t('wallet.availableBalance')}
          value={balance}
          currency={currency}
          icon={<Wallet className="h-5 w-5" />}
        />
        <StatCard
          title={t('wallet.withdrawalHistory')}
          value={withdrawalHistory.length}
          icon={<ArrowDownToLine className="h-5 w-5" />}
        />
        <StatCard
          title={t('wallet.withdrawalAccounts')}
          value={withdrawalAccounts.length}
          icon={<Building2 className="h-5 w-5" />}
        />
      </div>

      {balance < 50 && !hasPending && (
        <div className="mb-4 rounded-lg border border-info/30 bg-info/5 p-3 text-sm text-info">
          <Wallet className="me-2 inline h-4 w-4" />
          {t('wallet.minBalance')}
        </div>
      )}

      {hasPending && (
        <div className="mb-4 rounded-lg border border-warning/30 bg-warning/5 p-3 text-sm text-warning">
          <Clock className="me-2 inline h-4 w-4" />
          {t('wallet.pendingExists')}
        </div>
      )}

      {/* Tabs + Export button row */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Tabs tabs={tabs} activeTab={tab} onChange={setTab} />
        {tab === 'transactions' && transactions?.length ? (
          <Button variant="secondary" onClick={handleExport}>
            <Download className="h-4 w-4" />
            {t('walletDetails.exportReport')}
          </Button>
        ) : null}
      </div>

      {/* Transactions tab */}
      {tab === 'transactions' && (
        <div className="space-y-4">
          {/* Earnings Chart */}
          {earningsData?.earnings && earningsData.earnings.length > 0 && (
            <div className="rounded-xl border border-border-default bg-bg-secondary p-5">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="flex items-center gap-2 text-sm font-semibold">
                  <TrendingUp className="h-4 w-4 text-accent" />
                  {t('wallet.earningsChart', 'Earnings')}
                </h2>
                <div className="flex gap-1 rounded-lg bg-bg-tertiary p-0.5">
                  {([
                    { key: 'months' as const, label: t('wallet.monthly', 'Monthly'), icon: <Calendar className="h-3.5 w-3.5" /> },
                    { key: 'years' as const, label: t('wallet.yearly', 'Yearly'), icon: <TrendingUp className="h-3.5 w-3.5" /> },
                  ]).map(p => (
                    <button
                      key={p.key}
                      onClick={() => setChartPeriod(p.key)}
                      className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                        chartPeriod === p.key
                          ? 'bg-bg-secondary text-accent shadow-sm'
                          : 'text-text-muted hover:text-text-primary'
                      }`}
                    >
                      {p.icon}
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={earningsData.earnings} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="walletEarningsGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--color-accent)" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="var(--color-accent)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-default)" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(v: string) => chartPeriod === 'years' ? v : v.slice(5)}
                    tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
                    axisLine={{ stroke: 'var(--color-border-default)' }}
                    tickLine={false}
                  />
                  <YAxis
                    tickFormatter={(v: number) => formatCurrency(v, earningsData.currency || currency)}
                    tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
                    axisLine={false}
                    tickLine={false}
                    width={72}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'var(--color-bg-secondary)',
                      border: '1px solid var(--color-border-default)',
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                    labelFormatter={(label: string) => label}
                    formatter={(value: number, name: string) => {
                      if (name === 'revenue') return [formatCurrency(value, earningsData.currency || currency), t('wallet.revenue', 'Revenue')];
                      return [value, name];
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke="var(--color-accent)"
                    strokeWidth={2}
                    fill="url(#walletEarningsGrad)"
                    dot={false}
                    activeDot={{ r: 4, strokeWidth: 0, fill: 'var(--color-accent)' }}
                  />
                </AreaChart>
              </ResponsiveContainer>
              <div className="mt-3 flex justify-center gap-6 border-t border-border-default pt-3 text-xs text-text-muted">
                <span>
                  {t('wallet.totalEarnings', 'Total')}: <span className="font-semibold text-text-primary">{formatCurrency(earningsData.earnings.reduce((s, e) => s + e.revenue, 0), earningsData.currency || currency)}</span>
                </span>
                <span>
                  {t('wallet.totalOrders', 'Orders')}: <span className="font-semibold text-text-primary">{earningsData.earnings.reduce((s, e) => s + e.orders, 0)}</span>
                </span>
              </div>
            </div>
          )}

          {/* Transactions list */}
          <div className="rounded-xl border border-border-default bg-bg-secondary">
          {txLoading ? (
            <div className="p-8"><LoadingSpinner /></div>
          ) : !transactions?.length ? (
            <p className="py-12 text-center text-sm text-text-muted">{t('wallet.noTransactions')}</p>
          ) : (
            <div className="divide-y divide-border-default">
              {transactions.map((tx) => {
                return (
                  <div key={tx._id} className="flex items-center justify-between px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent/10 text-accent">
                        <DollarSign className="h-4 w-4" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">{paymentLabel(tx.payment)}</p>
                        <p className="text-xs text-text-muted">
                          {tx.createdAt ? formatDateTime(tx.createdAt, lang) : '-'}
                        </p>
                      </div>
                    </div>
                    <div className="text-end">
                      <p className="text-sm font-semibold">
                        <CurrencyDisplay amount={tx.amount} currency={tx.currency} />
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        </div>
      )}

      {/* Withdrawals tab */}
      {tab === 'withdrawals' && (
        <div className="rounded-xl border border-border-default bg-bg-secondary">
          {!withdrawalHistory.length ? (
            <p className="py-12 text-center text-sm text-text-muted">{t('wallet.noWithdrawals')}</p>
          ) : (
            <div className="divide-y divide-border-default">
              {withdrawalHistory.map((w) => (
                <div key={w._id} className="flex items-center justify-between px-5 py-4">
                  <div className="flex items-center gap-3">
                    {stateIcon(w.state)}
                    <div>
                      <p className="text-sm font-medium">
                        <CurrencyDisplay amount={w.amount} currency={w.currency} />
                      </p>
                      <p className="text-xs text-text-muted">
                        {w.withdrawalAccount
                          ? `${t(`wallet.${w.withdrawalAccount.category}`)} - ${w.withdrawalAccount.identifier}`
                          : '-'}
                      </p>
                    </div>
                  </div>
                  <div className="text-end">
                    <Badge variant={stateVariant(w.state)}>
                      {t(`wallet.${w.state}`)}
                    </Badge>
                    <p className="mt-1 text-xs text-text-muted">
                      {w.createdAt ? formatDateTime(w.createdAt, lang) : ''}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Withdraw modal */}
      <Modal
        isOpen={showWithdrawModal}
        onClose={() => { setShowWithdrawModal(false); setSelectedAccount(null); form.reset(); }}
        title={t('wallet.requestWithdrawal')}
        footer={
          <>
            <Button variant="secondary" onClick={() => { setShowWithdrawModal(false); setSelectedAccount(null); form.reset(); }}>
              {t('common.cancel')}
            </Button>
            <Button
              isLoading={withdrawMutation.isPending}
              onClick={form.handleSubmit(onSubmitWithdraw)}
            >
              {t('wallet.requestWithdrawal')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {/* Balance reminder */}
          <div className="rounded-lg bg-bg-tertiary p-3 text-center">
            <p className="text-xs text-text-muted">{t('wallet.availableBalance')}</p>
            <p className="mt-1 text-xl font-bold"><CurrencyDisplay amount={balance} currency={currency} /></p>
          </div>

          {/* Existing accounts */}
          {withdrawalAccounts.length > 0 && (
            <div>
              <label className="mb-1.5 block text-sm font-medium">{t('wallet.useExisting')}</label>
              <div className="space-y-2">
                {withdrawalAccounts.map((acc) => (
                  <button
                    key={acc._id}
                    type="button"
                    onClick={() => selectExistingAccount(acc)}
                    className={`flex w-full items-center justify-between rounded-lg border p-3 text-start text-sm transition-colors ${
                      selectedAccount === acc._id
                        ? 'border-accent bg-accent/5'
                        : 'border-border-default hover:border-accent/30 hover:bg-bg-hover'
                    }`}
                  >
                    <div>
                      <p className="font-medium">{acc.legalName}</p>
                      <p className="text-xs text-text-muted">
                        {t(`wallet.${acc.category}`)} - {acc.identifier}
                      </p>
                    </div>
                    <Badge>{t(`wallet.${acc.category}`)}</Badge>
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => { setSelectedAccount(null); form.reset(); }}
                  className={`flex w-full items-center justify-center rounded-lg border p-2.5 text-sm transition-colors ${
                    !selectedAccount
                      ? 'border-accent bg-accent/5 text-accent'
                      : 'border-border-default text-text-muted hover:border-accent/30'
                  }`}
                >
                  {t('wallet.newAccount')}
                </button>
              </div>
            </div>
          )}

          {/* Account category */}
          <div>
            <label className="mb-1.5 block text-sm font-medium">{t('wallet.accountType')}</label>
            <div className="grid grid-cols-3 gap-2">
              {ACCOUNT_CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => { form.setValue('accountCategory', cat); setSelectedAccount(null); }}
                  className={`rounded-lg border p-2.5 text-center text-sm transition-colors ${
                    watchCategory === cat && !selectedAccount
                      ? 'border-accent bg-accent/5 text-accent'
                      : 'border-border-default text-text-secondary hover:border-accent/30'
                  }`}
                >
                  {t(`wallet.${cat}`)}
                </button>
              ))}
            </div>
          </div>

          {/* Account identifier */}
          <div>
            <label className="mb-1.5 block text-sm font-medium">
              {watchCategory === 'paypal' ? t('common.email') : t('wallet.iban', 'IBAN')}
            </label>
            <input
              {...form.register('accountIdentifier')}
              className="w-full rounded-lg border border-border-default bg-bg-primary px-3 py-2 text-sm outline-none focus:border-accent"
              placeholder={watchCategory === 'paypal' ? 'email@example.com' : 'SA...'}
              dir="ltr"
            />
            {form.formState.errors.accountIdentifier && (
              <p className="mt-1 text-xs text-danger">{t('common.required')}</p>
            )}
          </div>

          {/* Legal name */}
          <div>
            <label className="mb-1.5 block text-sm font-medium">{t('wallet.accountHolder')}</label>
            <input
              {...form.register('legalName')}
              className="w-full rounded-lg border border-border-default bg-bg-primary px-3 py-2 text-sm outline-none focus:border-accent"
            />
            {form.formState.errors.legalName && (
              <p className="mt-1 text-xs text-danger">{t('common.required')}</p>
            )}
          </div>

          {/* Bank name (only for otherbank) */}
          {watchCategory === 'otherbank' && (
            <div>
              <label className="mb-1.5 block text-sm font-medium">{t('wallet.bankName')}</label>
              <input
                {...form.register('bankName')}
                className="w-full rounded-lg border border-border-default bg-bg-primary px-3 py-2 text-sm outline-none focus:border-accent"
              />
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
